import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { ReactNode } from 'react';
import { AUTURA_ASPHALT_LIGHT } from 'context/themeContext';
import { capitalizeFirstLetter } from 'utils/stringFormatters';
import { useTranslation } from 'react-i18next';

export interface AuturaSwitchWithOptionProps {
  name: string;
  disabled?: boolean;
  readonly?: boolean;
  switchValue: boolean;
  setSwitchValue?: (value: boolean) => void;
  checkboxValue: boolean;
  setCheckboxValue?: (value: boolean) => void;
  label?: string;
  sx?: SxProps;
  helperText?: string;
}

const AuturaSwitchWithOption = ({
  name,
  disabled,
  label = '',
  helperText,
  readonly,
  switchValue,
  setSwitchValue,
  checkboxValue,
  setCheckboxValue,
  sx,
}: AuturaSwitchWithOptionProps): ReactNode => {
  const { palette } = useTheme();
  const { t } = useTranslation();

  const handleSwitchChange = () => {
    if (setSwitchValue) setSwitchValue(!switchValue);
  };

  const handleCheckboxChange = () => {
    if (setCheckboxValue) setCheckboxValue(!checkboxValue);
  };

  return (
    <Stack
      direction={'column'}
      display={'flex'}
      justifyContent={'flex-start'}
      alignContent={'flex-start'}
      sx={{ height: 60, pl: 1.25, ...sx }}
    >
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ height: 50 }}
      >
        <FormControlLabel
          label={label || ''}
          disabled={disabled}
          control={
            <Box
              sx={{
                width: '60px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Switch
                checked={switchValue}
                name={name}
                readOnly={readonly}
                disabled={disabled}
                color={'primary'}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: palette.primary.main,
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: disabled
                      ? AUTURA_ASPHALT_LIGHT
                      : switchValue
                        ? palette.primary.main
                        : AUTURA_ASPHALT_LIGHT,
                  },
                  '& .MuiSwitch-thumb': {
                    backgroundColor: disabled
                      ? AUTURA_ASPHALT_LIGHT
                      : switchValue
                        ? palette.primary.main
                        : AUTURA_ASPHALT_LIGHT,
                  },
                }}
                onChange={handleSwitchChange}
              />
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={checkboxValue}
              name={`${name}-required`}
              disabled={disabled || !switchValue}
              color='primary'
              onChange={handleCheckboxChange}
              size='small'
              sx={{
                mb: '2px',
                ml: 2,
              }}
            />
          }
          label={t('required')}
          disabled={disabled || !switchValue}
        />
      </Stack>
      <Typography
        variant='caption'
        ml={'50px'}
        lineHeight={1}
        sx={{
          mt: -1,
          pl: 1.5,
          opacity: disabled ? 0.5 : 0.8,
        }}
      >
        {capitalizeFirstLetter(helperText)}
      </Typography>
    </Stack>
  );
};

export default AuturaSwitchWithOption;
